import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axiosInstance from 'api/axiosInstance';
import apiUrls from 'api/apiUrls';
import {
  Box,
  Button,
  Checkbox,
  Flex,
  HStack,
  Input,
  Select,
  Text,
  useColorModeValue,
  useToast,
  Switch,
  ModalFooter,
  Stack,
  Tooltip,
  SimpleGrid,
  Heading,
} from '@chakra-ui/react';
import {
  TriangleUpIcon,
  TriangleDownIcon,
  DownloadIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@chakra-ui/icons';
import dayjs from 'dayjs';
import { useParams } from 'react-router-dom';
import TelemetryChart from './charts';
import { v4 as uuidv4 } from 'uuid';
import Card from 'components/card/Card';
import { MdTableChart, MdShowChart } from 'react-icons/md';
// import { Skeleton, SkeletonText } from '@chakra-ui/react';
import { useLocation } from 'react-router-dom';
import GlobalModal from 'components/GlobalModal';
import ComplexTable from '../default/components/ComplexTable';
import InterfaceSelect from 'components/select';
import ViewMoreModal from 'components/ViewMoreModal';

const TelemetryMonitor = () => {
  const platformId = process.env.REACT_APP_PLATFORM_ID;
  const groupID = localStorage.getItem('groupID');

  const toast = useToast();
  const location = useLocation();
  const { deviceUUID } = useParams();
  const deviceId = localStorage.getItem('deviceId');

  const [slaves, setSlaves] = useState([]);
  const [parametersBySlave, setParametersBySlave] = useState({});
  const [selectedSlaveId, setSelectedSlaveId] = useState('');
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [visibleColumns, setVisibleColumns] = useState({ timestamp: true });

  const [timeFilter, setTimeFilter] = useState('1h');
  const [customRange, setCustomRange] = useState({ start: '', end: '' });
  const [live, setLive] = useState(true);

  const [telemetryData, setTelemetryData] = useState([]);
  const [isFetching, setIsFetching] = useState(false);

  const [sortConfig, setSortConfig] = useState({
    key: 'timestamp',
    direction: 'desc',
  });
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;

  const [viewMode, setViewMode] = useState('table');
  const [isKeyModalOpen, setIsKeyModalOpen] = useState(false);

  // Theme colors
  const textColor = useColorModeValue('gray.700', 'gray.200');
  const hoverBg = useColorModeValue('navy.50', 'navy.700');
  const selectBg = useColorModeValue('white', 'black');
  const selectColor = useColorModeValue('black', 'white');

  useEffect(() => {
    if (!selectedSlaveId) return;
    const keys = (parametersBySlave[selectedSlaveId] || []).slice(0, 10);
    const validKeys = parametersBySlave[selectedSlaveId] || [];
    const filteredKeys = keys.filter((k) => validKeys.includes(k));
    setSelectedKeys(filteredKeys);
    setVisibleColumns({
      timestamp: true,
      ...filteredKeys.reduce((acc, k) => ({ ...acc, [k]: true }), {}),
    });
    // setCurrentPage(1);
  }, [selectedSlaveId, parametersBySlave]);

  // Load selected keys from localStorage for the selected slave
  useEffect(() => {
    if (!selectedSlaveId) return;
    const stored = localStorage.getItem(`selectedKeys_${selectedSlaveId}`);
    let keys;
    if (stored) {
      try {
        keys = JSON.parse(stored);
      } catch {
        keys = (parametersBySlave[selectedSlaveId] || []).slice(0, 10);
      }
    } else {
      keys = (parametersBySlave[selectedSlaveId] || []).slice(0, 10);
    }
    const validKeys = parametersBySlave[selectedSlaveId] || [];
    const filteredKeys = keys.filter((k) => validKeys.includes(k));
    setSelectedKeys(filteredKeys);
    setVisibleColumns({
      timestamp: true,
      ...filteredKeys.reduce((acc, k) => ({ ...acc, [k]: true }), {}),
    });
    // setCurrentPage(1);
  }, [selectedSlaveId, parametersBySlave]);

  // Store selected keys in localStorage whenever they change
  useEffect(() => {
    if (selectedSlaveId && selectedKeys) {
      localStorage.setItem(
        `selectedKeys_${selectedSlaveId}`,
        JSON.stringify(selectedKeys),
      );
    }
  }, [selectedSlaveId, selectedKeys]);

  // 1. Load slaves and their parameters
  const loadConfig = useCallback(async () => {
    if (!deviceId) return;

    try {
      const keys = 'modbus_config';
      const resp = await axiosInstance.get(
        apiUrls.toolDeviceAttributes(deviceId, keys),
      );

      const attrMap = Object.fromEntries(
        resp.data.map((a) => [a.key, a.value]),
      );
      const modbus = attrMap.modbus_config;
      if (!modbus?.slave_list?.length) return;

      const uuids = modbus.slave_list.map((s) => s.uuid).join(',');
      const metaResp = await axiosInstance.get(
        apiUrls.toolDeviceAttributes(deviceId, uuids),
      );
      const metaMap = Object.fromEntries(
        metaResp.data.map((a) => [a.key, a.value]),
      );

      const cfg = modbus.slave_list.map((s) => {
        const meta = metaMap[s.uuid] || {};
        const params = (meta.parameters || []).map((p) => ({
          ...p,
          id: p.id || uuidv4(),
        }));
        return { id: s.uuid, name: meta.name || s.uuid, parameters: params };
      });

      setSlaves(cfg);

      const map = cfg.reduce((acc, s) => {
        acc[s.id] = s.parameters.map((p) => p.name);
        return acc;
      }, {});
      setParametersBySlave(map);

      if (cfg.length) {
        const first = cfg[0].id;
        setSelectedSlaveId(first);
        const keys = (map[first] || []).slice(0, 10);
        setSelectedKeys(keys);
        setVisibleColumns({
          timestamp: true,
          ...keys.reduce((a, k) => ({ ...a, [k]: true }), {}),
        });
      }
    } catch (err) {
      toast({
        title: 'Error loading config',
        description: err.message,
        status: 'error',
      });
    }
  }, [deviceId, toast]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const fetchData = useCallback(async () => {
    const getRange = () => {
      const now = Date.now();
      const mapH = {
        '5m': 5 / 60,
        '10m': 10 / 60,
        '15m': 15 / 60,
        '30m': 30 / 60,
        '1h': 1,
        '2h': 2,
        '3h': 3,
        '4h': 4,
        '6h': 6,
        '12h': 12,
        '1d': 24,
        '2d': 48,
      };
      return timeFilter !== 'custom'
        ? { start: now - mapH[timeFilter] * 3600000, end: now }
        : {
            start: Date.parse(customRange.start),
            end: Date.parse(customRange.end),
          };
    };

    if (!selectedSlaveId || !selectedKeys.length) {
      setTelemetryData([]);
      return;
    }
    // const token = localStorage.getItem('authToken');
    // if (!token) return;

    const { start, end } = getRange();
    if (!start || !end || start >= end) {
      setTelemetryData([]);
      return;
    }

    setIsFetching(true);

    try {
      const keys = selectedKeys.join(',');

      const diff = end - start; // milliseconds

      const TARGET_POINTS = 400;

      const interval = Math.max(Math.floor(diff / TARGET_POINTS), 60000); // min 1 minute
      const limit = TARGET_POINTS;

      const resp = await axiosInstance.get(
        apiUrls.deviceTsData(deviceUUID, start, end, limit, interval, keys),
      );

      const tsMap = new Map();

      selectedKeys.forEach((key) => {
        (resp.data[key] || []).forEach(({ ts, value }) => {
          ts = Math.floor(Number(ts) / 1000) * 1000;

          if (!tsMap.has(ts)) tsMap.set(ts, { timestamp: ts });
          tsMap.get(ts)[key] = value;
        });
      });

      const merged = Array.from(tsMap.values()).sort(
        (a, b) => b.timestamp - a.timestamp,
      );

      setTelemetryData(merged);
      // setCurrentPage(1);s
    } catch (err) {
      toast({
        title: 'Fetch error',
        description: err.message,
        status: 'error',
      });
    } finally {
      setIsFetching(false);
    }
  }, [
    selectedSlaveId,
    selectedKeys,
    toast,
    deviceUUID,
    timeFilter,
    customRange,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (live) {
      const id = setInterval(fetchData, 30000);
      return () => clearInterval(id);
    }
  }, [live, fetchData]);

  // 4. Sort + paginate
  const sorted = useMemo(() => {
    return [...telemetryData].sort((a, b) => {
      if (sortConfig.key === 'timestamp') {
        return sortConfig.direction === 'asc'
          ? a.timestamp - b.timestamp
          : b.timestamp - a.timestamp;
      }
      const aVal = parseFloat(a[sortConfig.key] ?? 0);
      const bVal = parseFloat(b[sortConfig.key] ?? 0);
      return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
    });
  }, [telemetryData, sortConfig]);

  const pageData = useMemo(() => {
    const startIdx = (currentPage - 1) * rowsPerPage;
    return sorted.slice(startIdx, startIdx + rowsPerPage);
  }, [sorted, currentPage]);

  const handleSort = (key) => {
    setSortConfig((prev) =>
      prev.key === key
        ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: 'asc' },
    );
  };

  const exportCSV = () => {
    const cols = Object.keys(visibleColumns).filter((k) => visibleColumns[k]);
    const rows = telemetryData.map((row) =>
      cols
        .map((c) =>
          c === 'timestamp'
            ? dayjs(row[c]).format('YYYY-MM-DD HH:mm:ss')
            : (row[c] ?? '-'),
        )
        .join(','),
    );
    const csv = [cols.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'telemetry.csv';
    a.click();
  };

  const resetFilters = () => {
    setTimeFilter('1h');
    setCustomRange({ start: '', end: '' });
    setLive(true);
    const keys = parametersBySlave[selectedSlaveId] || [];
    setSelectedKeys(keys);
    setVisibleColumns({
      timestamp: true,
      ...keys.reduce((a, k) => ({ ...a, [k]: true }), {}),
    });
    // Also update localStorage on reset
    localStorage.setItem(
      `selectedKeys_${selectedSlaveId}`,
      JSON.stringify(keys),
    );
  };

  // 5. Compute stats
  const summaryStats = useMemo(() => {
    return selectedKeys.reduce((acc, key) => {
      const vals = telemetryData
        .map((row) => row[key])
        .filter((v) => v !== undefined)
        .map((v) => parseFloat(v));
      if (vals.length) {
        const min = Math.min(...vals).toFixed(2);
        const max = Math.max(...vals).toFixed(2);
        const avg = (vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(2);
        acc[key] = { min, max, avg };
      }
      return acc;
    }, {});
  }, [telemetryData, selectedKeys]);

  const createColumns = () => [
    {
      id: 'timestamp',
      header: 'Timestamp',
      accessorKey: 'timestamp',
      cell: ({ row }) =>
        dayjs(row.original.timestamp).format('MMM D, YYYY HH:mm:ss'),
    },
    ...selectedKeys.map((key) => ({
      id: key,
      header: key,
      accessorKey: key,
      cell: ({ row }) => row.original[key] ?? '-',
    })),
  ];

  const fetchTelemetryKeys = useCallback(() => {
    axiosInstance
      .get(apiUrls.fetchAllTelemetryKeys(platformId, groupID))
      .then((response) => {
        console.log('Full Response:', response); // full response
        console.log('Data:', response.data); // only data
        console.log('Devices:', response.data.results); // results array

        const devices = response.data.results || [];
      })
      .catch((error) => {
        console.error('Error fetching Modbus Configs:', error);
      });
  }, [platformId, groupID]);

  useEffect(() => {
    fetchTelemetryKeys();
  }, [fetchTelemetryKeys]);

  return (
    <Box color={textColor}>
      <Heading size="lg">Monitoring</Heading>
      <Card p={4} mb={6} mt={4}>
        <Flex
          w="100%"
          wrap="wrap"
          align="center"
          justifyContent="space-between"
          gap={4}
        >
          {/* Left side controls */}
          <HStack spacing={4} flexWrap="wrap" minWidth="300px">
            <InterfaceSelect
              border="2px solid lightgray"
              _focus={{ boxShadow: 'none', borderColor: 'navy.500' }}
              width="180px"
              options={
                slaves?.map((s) => ({
                  label: s.name,
                  value: s.id,
                })) || []
              }
              value={selectedSlaveId}
              onChange={(val) => setSelectedSlaveId(val)}
            >
              {slaves.map((s) => (
                <option
                  style={{
                    background: `${selectBg}`,
                    color: `${selectColor}`,
                  }}
                  key={s.id}
                  value={s.id}
                >
                  {s.name}
                </option>
              ))}
            </InterfaceSelect>

            <Button
              variant="outline"
              border="2px"
              colorScheme="green"
              hoverBg={hoverBg}
              onClick={() => setIsKeyModalOpen(true)}
            >
              Select Keys ({selectedKeys.length})
            </Button>

            <Button
              variant="outline"
              border="2px"
              colorScheme="teal"
              hoverBg={hoverBg}
              onClick={exportCSV}
              leftIcon={<DownloadIcon />}
            >
              Export CSV
            </Button>
            <InterfaceSelect
              width="140px"
              border="2px solid lightgray"
              value={timeFilter}
              options={[
                '5m',
                '10m',
                '15m',
                '30m',
                '1h',
                '2h',
                '3h',
                '4h',
                '6h',
                '12h',
                '1d',
                '2d',
                'custom',
              ]}
              onChange={(val) => setTimeFilter(val)}
            />

            {timeFilter === 'custom' && (
              <HStack
                spacing={3}
                p={3}
                borderWidth="1px"
                borderRadius="md"
                bg="gray.50"
                align="center"
                flexWrap="wrap"
              >
                <Input
                  type="datetime-local"
                  size="sm"
                  value={customRange.start}
                  maxW="200px"
                  bg="white"
                  borderRadius="md"
                  onChange={(e) =>
                    setCustomRange((prev) => ({
                      ...prev,
                      start: e.target.value,
                    }))
                  }
                />

                <Text fontSize="sm" color="gray.500" fontWeight="medium">
                  to
                </Text>

                <Input
                  type="datetime-local"
                  size="sm"
                  value={customRange.end}
                  maxW="200px"
                  bg="white"
                  borderRadius="md"
                  onChange={(e) =>
                    setCustomRange((prev) => ({
                      ...prev,
                      end: e.target.value,
                    }))
                  }
                />
              </HStack>
            )}
          </HStack>

          {/* Right side controls */}
          <HStack
            spacing={4}
            flexWrap="wrap"
            minWidth="300px"
            justifyContent="flex-end"
          >
            <Button
              variant="outline"
              border="2px"
              colorScheme="navy"
              onClick={fetchData}
              isLoading={isFetching}
            >
              Fetch
            </Button>

            <HStack>
              <Switch
                isChecked={live}
                onChange={(e) => setLive(e.target.checked)}
              />
              <Text>Live</Text>
            </HStack>

            <Button
              variant="outline"
              border="2px"
              onClick={resetFilters}
              colorScheme="orange"
            >
              Reset
            </Button>

            <HStack
              border="2px solid"
              borderColor="gray.300"
              borderRadius="0.5rem"
              overflow="hidden"
              spacing={0}
              w="fit-content"
            >
              <Tooltip label="Table View" openDelay={500}>
                <Button
                  leftIcon={<MdTableChart />}
                  colorScheme={viewMode === 'table' ? 'purple' : 'gray'}
                  variant={viewMode === 'table' ? 'solid' : 'ghost'}
                  onClick={() => setViewMode('table')}
                  borderRadius="0"
                  px={4}
                  size="sm"
                >
                  Table
                </Button>
              </Tooltip>

              <Tooltip label="Chart View" openDelay={500}>
                <Button
                  leftIcon={<MdShowChart />}
                  colorScheme={viewMode === 'chart' ? 'purple' : 'gray'}
                  variant={viewMode === 'chart' ? 'solid' : 'ghost'}
                  onClick={() => setViewMode('chart')}
                  borderRadius="0"
                  px={4}
                  size="sm"
                >
                  Chart
                </Button>
              </Tooltip>
            </HStack>
          </HStack>
        </Flex>
        {/* Key selection modal */}

        <ViewMoreModal
          isOpen={isKeyModalOpen}
          onClose={() => setIsKeyModalOpen(false)}
          title="Select Keys"
          width="1200px"
          size="xxl"
          items={parametersBySlave[selectedSlaveId] || []}
          pageSize={16}
          renderItem={(key) => (
            <Checkbox
              key={key}
              isChecked={selectedKeys.includes(key)}
              onChange={() => {
                const newKeys = selectedKeys.includes(key)
                  ? selectedKeys.filter((k) => k !== key)
                  : [...selectedKeys, key];
                setSelectedKeys(newKeys);
                setVisibleColumns((prev) => ({
                  ...prev,
                  [key]: !prev[key],
                }));
              }}
            >
              {key}
            </Checkbox>
          )}
          extraHeader={
            <Flex align="center" gap={2} justify="center">
              <Checkbox
                isChecked={
                  parametersBySlave[selectedSlaveId]?.length > 0 &&
                  selectedKeys.length ===
                    parametersBySlave[selectedSlaveId]?.length
                }
                isIndeterminate={
                  selectedKeys.length > 0 &&
                  selectedKeys.length <
                    parametersBySlave[selectedSlaveId]?.length
                }
                onChange={(e) => {
                  if (e.target.checked) {
                    const allKeys = parametersBySlave[selectedSlaveId] || [];
                    setSelectedKeys(allKeys);
                    setVisibleColumns({
                      timestamp: true,
                      ...allKeys.reduce(
                        (acc, k) => ({ ...acc, [k]: true }),
                        {},
                      ),
                    });
                  } else {
                    setSelectedKeys([]);
                    setVisibleColumns({ timestamp: true });
                  }
                }}
              >
                Select All
              </Checkbox>
            </Flex>
          }
        />
      </Card>

      {/* Data Display */}
      {viewMode === 'table' && (
        <>
          <ComplexTable
            tableData={telemetryData}
            columnsData={createColumns()}
            loading={isFetching}
            hasNext={false}
            title="Telemetry Data"
            enableLocalPagination={true}
            pageSize={10}
          />

          {/* Statistics Table */}
          {/* {selectedKeys.length > 0 && (
            <Card p={4} mt={6}>
              <Text fontSize="lg" fontWeight="bold" mb={3}>
                Summary Statistics
              </Text>
              <Table variant="simple">
                <Thead>
                  <Tr>
                    <Th borderColor="transparent">Key</Th>
                    <Th borderColor="transparent" isNumeric>
                      Average
                    </Th>
                    <Th borderColor="transparent" isNumeric>
                      Min
                    </Th>
                    <Th borderColor="transparent" isNumeric>
                      Max
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {selectedKeys.map((key) => {
                    const stats = summaryStats[key];
                    return stats ? (
                      <Tr key={key} _hover={{ bg: hoverBg }}>
                        <Td borderColor="transparent">{key}</Td>
                        <Td borderColor="transparent" isNumeric>
                          {stats.avg}
                        </Td>
                        <Td borderColor="transparent" isNumeric>
                          {stats.min}
                        </Td>
                        <Td borderColor="transparent" isNumeric>
                          {stats.max}
                        </Td>
                      </Tr>
                    ) : null;
                  })}
                </Tbody>
              </Table>
            </Card>
          )} */}
        </>
      )}

      {viewMode === 'chart' && (
        <>
          <TelemetryChart
            telemetryData={telemetryData}
            selectedKeysDevice={selectedKeys}
            deviceId={deviceUUID}
            selectedSlaveId={selectedSlaveId}
          />
        </>
      )}
    </Box>
  );
};

export default TelemetryMonitor;
